import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Search, RefreshCw, X, Trash2, Save, Mail, Phone, Calendar, MapPin,
  Compass, Inbox, UserCheck, ArrowRight, ChevronRight, Loader2,
} from 'lucide-react';
import { GlassPanel, StatBadge } from './ui';

/**
 * Travel Agency CRM — dark, Apple-inspired. Organizes all customer-submitted
 * data from zeratravel.com into editable, movable, deletable workflows.
 */

type SubKey = 'booking' | 'journey' | 'touchpoint' | 'newsletter';

interface Lead {
  id: string;
  kind: 'booking' | 'journey';
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  destination?: string;
  experience?: string;
  start?: string | null;
  end?: string | null;
  guests?: string;
  budget?: string;
  notes?: string;
  status: string;
  created_at: string;
}

interface NewsletterRow {
  id: string;
  email: string;
  source: string;
  created_at: string;
}

// Booking Process steps → status values
const STEPS: { id: string; label: string; code: string }[] = [
  { id: 'new_booking', label: 'Booking Request', code: '1a' },
  { id: 'new_journey', label: 'Journey Request', code: '1b' },
  { id: 'touchpoint', label: 'Touchpoint Requested', code: '2a' },
  { id: 'contacted', label: 'Contact Made', code: '2b' },
  { id: 'booked', label: 'Trip Booked', code: '3a' },
  { id: 'future', label: 'Future Trips', code: '3b' },
];

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const stepForLead = (l: Lead): string => {
  const s = (l.status || 'new').toLowerCase();
  if (['touchpoint', 'contacted', 'booked', 'confirmed', 'future'].includes(s)) {
    return s === 'confirmed' ? 'booked' : s;
  }
  return l.kind === 'journey' ? 'new_journey' : 'new_booking';
};

const CrmSection: React.FC = () => {
  const [sub, setSub] = useState<SubKey>('booking');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [newsletter, setNewsletter] = useState<NewsletterRow[]>([]);
  const [selected, setSelected] = useState<Lead | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    const [j, b, n] = await Promise.all([
      supabase.from('journey_requests').select('*').order('created_at', { ascending: false }),
      supabase.from('booking_requests').select('*').order('created_at', { ascending: false }),
      supabase.from('newsletter_subscribers').select('*').order('created_at', { ascending: false }),
    ]);
    const jl: Lead[] = (j.data || []).map((r: any) => ({
      id: r.id, kind: 'journey', first_name: r.first_name, last_name: r.last_name,
      email: r.email, phone: r.phone, destination: r.destination, experience: r.experience,
      start: r.check_in, end: r.check_out, guests: r.guests, budget: r.budget,
      notes: r.special_requests, status: r.status || 'new', created_at: r.created_at,
    }));
    const bl: Lead[] = (b.data || []).map((r: any) => ({
      id: r.id, kind: 'booking', first_name: r.first_name, last_name: r.last_name,
      email: r.email, phone: r.phone, experience: r.experience, destination: r.experience,
      start: r.arrival_date, end: r.departure_date, guests: r.guests, budget: r.budget,
      notes: r.special_requests, status: r.status || 'new', created_at: r.created_at,
    }));
    setLeads([...jl, ...bl].sort((a, b2) => +new Date(b2.created_at) - +new Date(a.created_at)));
    setNewsletter((n.data || []) as NewsletterRow[]);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const tableFor = (kind: Lead['kind']) => (kind === 'journey' ? 'journey_requests' : 'booking_requests');

  const moveLead = async (lead: Lead, stepId: string) => {
    // map step id back to a stored status value
    const statusMap: Record<string, string> = {
      new_booking: 'new', new_journey: 'new', touchpoint: 'touchpoint',
      contacted: 'contacted', booked: 'booked', future: 'future',
    };
    const status = statusMap[stepId] || 'new';
    await supabase.from(tableFor(lead.kind)).update({ status }).eq('id', lead.id);
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    if (selected?.id === lead.id) setSelected({ ...selected, status });
  };

  const saveLead = async (lead: Lead, patch: Partial<Lead>) => {
    const col = lead.kind === 'journey'
      ? { first_name: patch.first_name, last_name: patch.last_name, email: patch.email, phone: patch.phone, special_requests: patch.notes }
      : { first_name: patch.first_name, last_name: patch.last_name, email: patch.email, phone: patch.phone, special_requests: patch.notes };
    await supabase.from(tableFor(lead.kind)).update(col).eq('id', lead.id);
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...patch } : l)));
  };

  const deleteLead = async (lead: Lead) => {
    if (!confirm('Permanently delete this record?')) return;
    await supabase.from(tableFor(lead.kind)).delete().eq('id', lead.id);
    setLeads((prev) => prev.filter((l) => l.id !== lead.id));
    setSelected(null);
  };

  const deleteSubscriber = async (id: string) => {
    if (!confirm('Remove this subscriber?')) return;
    await supabase.from('newsletter_subscribers').delete().eq('id', id);
    setNewsletter((prev) => prev.filter((n) => n.id !== id));
  };

  const q = search.trim().toLowerCase();
  const matchLead = (l: Lead) =>
    !q || `${l.first_name} ${l.last_name} ${l.email} ${l.destination} ${l.experience}`.toLowerCase().includes(q);

  const filteredLeads = useMemo(() => leads.filter(matchLead), [leads, q]);

  const subs: { key: SubKey; label: string; icon: React.ComponentType<{ className?: string }>; count: number }[] = [
    { key: 'booking', label: 'Booking Process', icon: Inbox, count: leads.length },
    { key: 'journey', label: 'Journey Requests', icon: Compass, count: leads.filter((l) => l.kind === 'journey').length },
    { key: 'touchpoint', label: 'Touchpoint Requests', icon: UserCheck, count: leads.filter((l) => ['touchpoint', 'contacted'].includes(l.status)).length },
    { key: 'newsletter', label: 'Newsletter / Blog Room', icon: Mail, count: newsletter.length },
  ];

  return (
    <div>
      {/* Subsection nav */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {subs.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setSub(s.key)}
                className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-medium transition-all ${
                  sub === s.key ? 'bg-white text-black' : 'border border-white/15 text-white/60 hover:text-white'
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {s.label}
                <span className={`rounded-full px-1.5 text-[10px] ${sub === s.key ? 'bg-black/10' : 'bg-white/10'}`}>{s.count}</span>
              </button>
            );
          })}
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-xs text-white/60 hover:text-white"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Search */}
      {sub !== 'newsletter' && (
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, destination…"
            className="w-full rounded-full border border-white/10 bg-white/[0.04] py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none"
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-24 text-white/40">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading customer data…
        </div>
      ) : sub === 'booking' ? (
        <BookingBoard leads={filteredLeads} onSelect={setSelected} onMove={moveLead} />
      ) : sub === 'journey' ? (
        <LeadList leads={filteredLeads.filter((l) => l.kind === 'journey')} onSelect={setSelected} />
      ) : sub === 'touchpoint' ? (
        <LeadList leads={filteredLeads.filter((l) => ['touchpoint', 'contacted'].includes(l.status))} onSelect={setSelected} />
      ) : (
        <NewsletterList rows={newsletter} onDelete={deleteSubscriber} />
      )}

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onSave={saveLead}
          onDelete={deleteLead}
          onMove={moveLead}
        />
      )}
    </div>
  );
};

// ===== Booking Process kanban board =====
const BookingBoard: React.FC<{
  leads: Lead[];
  onSelect: (l: Lead) => void;
  onMove: (l: Lead, step: string) => void;
}> = ({ leads, onSelect, onMove }) => (
  <div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-6">
    {STEPS.map((step) => {
      const col = leads.filter((l) => stepForLead(l) === step.id);
      return (
        <GlassPanel key={step.id} className="flex flex-col p-3">
          <div className="mb-3 flex items-center justify-between px-1">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A961]">Step {step.code}</div>
              <div className="text-sm font-medium text-white">{step.label}</div>
            </div>
            <StatBadge>{col.length}</StatBadge>
          </div>
          <div className="space-y-2">
            {col.length === 0 && <div className="rounded-lg border border-dashed border-white/10 py-6 text-center text-[11px] text-white/25">Empty</div>}
            {col.map((l) => {
              const idx = STEPS.findIndex((s) => s.id === step.id);
              const next = STEPS[Math.min(idx + 1, STEPS.length - 1)];
              return (
                <div key={l.id} className="group rounded-xl border border-white/10 bg-white/[0.03] p-3 transition-colors hover:border-white/25">
                  <button onClick={() => onSelect(l)} className="block w-full text-left">
                    <div className="text-sm font-medium text-white">{l.first_name} {l.last_name}</div>
                    <div className="mt-0.5 truncate text-[11px] text-white/40">{l.destination || l.experience || l.email}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-wider text-white/30">{l.kind} · {fmtDate(l.created_at)}</div>
                  </button>
                  {next.id !== step.id && (
                    <button
                      onClick={() => onMove(l, next.id)}
                      className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-white/5 py-1.5 text-[10px] text-white/50 opacity-0 transition-opacity hover:bg-white/10 hover:text-white group-hover:opacity-100"
                    >
                      Move to {next.label} <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </GlassPanel>
      );
    })}
  </div>
);

// ===== Simple list =====
const LeadList: React.FC<{ leads: Lead[]; onSelect: (l: Lead) => void }> = ({ leads, onSelect }) => {
  if (!leads.length) return <GlassPanel className="py-20 text-center text-sm text-white/40">No records here yet.</GlassPanel>;
  return (
    <div className="space-y-2">
      {leads.map((l) => (
        <button
          key={l.id}
          onClick={() => onSelect(l)}
          className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4 text-left transition-colors hover:border-white/25"
        >
          <div>
            <div className="text-sm font-medium text-white">{l.first_name} {l.last_name}</div>
            <div className="text-[12px] text-white/40">{l.email}{l.phone ? ` · ${l.phone}` : ''}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden text-right sm:block">
              <div className="text-xs text-white/60">{l.destination || l.experience || '—'}</div>
              <div className="text-[11px] text-white/30">{fmtDate(l.start)} → {fmtDate(l.end)}</div>
            </div>
            <StatBadge tone="blue">{l.status}</StatBadge>
            <ChevronRight className="h-4 w-4 text-white/30" />
          </div>
        </button>
      ))}
    </div>
  );
};

const NewsletterList: React.FC<{ rows: NewsletterRow[]; onDelete: (id: string) => void }> = ({ rows, onDelete }) => {
  if (!rows.length) return <GlassPanel className="py-20 text-center text-sm text-white/40">No subscribers yet.</GlassPanel>;
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4">
          <div className="flex items-center gap-3">
            <Mail className="h-4 w-4 text-[#C9A961]" />
            <span className="text-sm text-white">{r.email}</span>
          </div>
          <div className="flex items-center gap-4">
            <StatBadge>{r.source || 'footer'}</StatBadge>
            <span className="text-[11px] text-white/30">{fmtDate(r.created_at)}</span>
            <button onClick={() => onDelete(r.id)} className="text-white/30 hover:text-red-400">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ===== Editable drawer =====
const LeadDrawer: React.FC<{
  lead: Lead;
  onClose: () => void;
  onSave: (l: Lead, patch: Partial<Lead>) => Promise<void>;
  onDelete: (l: Lead) => void;
  onMove: (l: Lead, step: string) => void;
}> = ({ lead, onClose, onSave, onDelete, onMove }) => {
  const [form, setForm] = useState({
    first_name: lead.first_name || '', last_name: lead.last_name || '',
    email: lead.email || '', phone: lead.phone || '', notes: lead.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const currentStep = stepForLead(lead);

  const field = (label: string, key: keyof typeof form, type = 'text') => (
    <div>
      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white focus:border-white/30 focus:outline-none"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative h-full w-full max-w-md overflow-y-auto border-l border-white/10 bg-[#0A0A0B]">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#0A0A0B]/90 px-6 py-5 backdrop-blur-xl">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">{lead.kind} record</div>
            <h3 className="mt-1 text-xl font-light text-white">{lead.first_name} {lead.last_name}</h3>
          </div>
          <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/60 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-6 p-6">
          <div>
            <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-white/40">Move to step</div>
            <div className="grid grid-cols-2 gap-2">
              {STEPS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => onMove(lead, s.id)}
                  className={`rounded-lg border px-3 py-2 text-[11px] transition-all ${
                    currentStep === s.id ? 'border-[#C9A961] bg-[#C9A961]/15 text-[#E4C77F]' : 'border-white/10 text-white/50 hover:border-white/30'
                  }`}
                >
                  {s.code} · {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {field('First name', 'first_name')}
              {field('Last name', 'last_name')}
            </div>
            {field('Email', 'email', 'email')}
            {field('Phone', 'phone', 'tel')}
            <div>
              <label className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-white/40">Notes / Requests</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="h-24 w-full resize-none rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white focus:border-white/30 focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-xs text-white/50">
            <div><span className="text-white/30">Dates</span><div className="text-white/70">{fmtDate(lead.start)} → {fmtDate(lead.end)}</div></div>
            <div><span className="text-white/30">Guests</span><div className="text-white/70">{lead.guests || '—'}</div></div>
            <div><span className="text-white/30">Budget</span><div className="text-white/70">{lead.budget || '—'}</div></div>
            <div><span className="text-white/30">Submitted</span><div className="text-white/70">{fmtDate(lead.created_at)}</div></div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={async () => { setSaving(true); await onSave(lead, form); setSaving(false); }}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-white py-3 text-xs font-medium text-black hover:bg-white/90"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </button>
            <a href={`mailto:${lead.email}`} className="flex items-center justify-center rounded-full border border-white/15 px-4 text-white/70 hover:text-white"><Mail className="h-4 w-4" /></a>
            {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center justify-center rounded-full border border-white/15 px-4 text-white/70 hover:text-white"><Phone className="h-4 w-4" /></a>}
            <button onClick={() => onDelete(lead)} className="flex items-center justify-center rounded-full border border-red-500/30 px-4 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmSection;
